import {Component, HostListener, OnInit, inject, TemplateRef, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {QuoteService} from "../../../services/quote.service";
import {DateUtils} from "../../../utils/date-utils";
import {TableData} from "../../../interfaces/table/table-data";
import {ToastService} from "../../../services/toast.service";
import {KeyValuePairTableData, KeyValuePairTableValueType} from "../../../helpers/key-value-pair-table-value-type";
import {TemplateAndValue} from "../../../helpers/template-and-value";
import {PageRedirect} from "../../../helpers/page-redirect";
import {awaitsCustomerAction, QuoteStatus} from "../../../enums/quoteStatus";

const P21_IMAGES_BASE_URL = 'https://biomed-p21-images.s3.us-east-1.amazonaws.com';

export function lineItemImageUrl(item: { qb_part_number?: string | null; line_item?: string | null }): string | null {
  const key = (item.qb_part_number || item.line_item || '').trim();
  return key ? `${P21_IMAGES_BASE_URL}/${encodeURIComponent(key)}.jpg` : null;
}

@Component({
  selector: 'app-quote',
  templateUrl: './quote.component.html',
  styleUrls: ['./quote.component.css'],
  standalone: false
})
export class QuoteComponent implements OnInit {
  quoteId: any = null;
  quoteDetails: any = null;
  quoteCustomFields: any[] = [];
  quoteDetailsFields: KeyValuePairTableData[] = [];
  activeTab: 'assets' | 'attachments' | 'comments' | 'history' = 'assets';
  isPdfExportRunning: boolean = false;
  currentUser: any;
  isDetailsLoaded: boolean | null = null;
  isDropdownOpen = false;
  hoveredQuoteId: string | null = null;

  quoteAssets: any[] = [];
  combinedAssetsDetails: any[] = [];
  quoteAssetsLineItems: any[] = []
  quoteAttachments: any[] = [];
  quoteComments: any[] = [];
  loadingComments: boolean = true;
  historyTableData: TableData = {data: []};
  loadingHistory: boolean = false;
  deselectedItems: Set<number> = new Set();
  brokenLineItemImages: Set<string> = new Set();

  @ViewChild('boldField', {static: true}) boldField!: TemplateRef<any>;
  @ViewChild('tagField', {static: true}) tagField!: TemplateRef<any>;
  @ViewChild('locationField', {static: true}) locationField!: TemplateRef<any>;
  @ViewChild('descriptionField', {static: true}) descriptionField!: TemplateRef<any>;

  protected readonly QuoteStatus = QuoteStatus;
  protected readonly awaitsCustomerAction = awaitsCustomerAction;
  private readonly activeRouter = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly quoteService = inject(QuoteService);
  private readonly toastService = inject(ToastService);

  constructor() {
    this.initQuoteId();
  }

  lineItemImageUrl(item: any): string | null {
    const url = lineItemImageUrl(item);
    return url && !this.brokenLineItemImages.has(url) ? url : null;
  }

  onLineItemImageError(url: string) {
    this.brokenLineItemImages.add(url);
  }

  async initQuoteId() {
    this.quoteId = await this.activeRouter.snapshot.queryParams['quote_id'];
    if (!this.quoteId) {
      this.returnToQuotes()
    }
  }

  ngOnInit() {
    Promise.all([
      this.getQuoteDetails(),
      this.getQuoteAsset(),
      this.getQuoteLineItem(),
      this.getQuoteHistories(),
      this.getQuoteAttachments(),
      this.getQuoteCustomFields(),
      this.getQuoteComments(),
    ]).then(() => {
      this.organizeAssetDetails();
      this.quoteDetailsFields = this.constructQuoteDetailsFields();
    });
  }

  /// Reload all Data on page for new quote
  loadQuoteDetails(quoteId: string) {
    this.quoteId = quoteId;
    this.isDetailsLoaded = null;
    this.quoteDetails = null;
    this.quoteAssets = [];
    this.quoteAssetsLineItems = [];
    this.quoteCustomFields = [];
    this.quoteComments = [];
    this.combinedAssetsDetails = [];
    this.isDropdownOpen = false;
    this.deselectedItems = new Set();

    Promise.all([
      this.getQuoteDetails(),
      this.getQuoteAsset(),
      this.getQuoteLineItem(),
      this.getQuoteHistories(),
      this.getQuoteCustomFields(),
      this.getQuoteComments(),
    ]).then(() => {
      this.organizeAssetDetails();
      this.quoteDetailsFields = this.constructQuoteDetailsFields();
    });
  }

  /// Generate Quote PDF
  openPdf() {
    this.isPdfExportRunning = true
    this.quoteService.GetQuotePdf(this.quoteId).then((res: any) => {
      if (res) {
        window.open(res.Data.Url, '_blank');
        this.isPdfExportRunning = false
      }
    })
  }

  //////GETTING ALL DATA
  /// COMMENTS
  async getQuoteComments() {
    this.loadingComments = true;
    this.quoteService.getQuoteComment(this.quoteId).then((data: any) => {
      this.loadingComments = false;
      if (!data || !data.Data) {
        return;
      }
      this.quoteComments = data.Data.map((x: any) => ({
        comment_id: x.comment_id,
        cp_user_id: x.cp_user_id,
        content: x.content,
        name: x.name,
        email: x.email,
        role_name: x.role,
        date: DateUtils.formatDateTime(x.created_dt)
      }));
    });
  }

  /// Quote Details
  async getQuoteDetails() {
    await this.quoteService.getQuoteDetails(this.quoteId).then((res: any) => {
      if (!res && res.length === 0) {
        this.isDetailsLoaded = false;
        return;
      }
      this.quoteDetails = res[0];

      this.quoteDetails.children_quote_ids = this.quoteDetails?.children_quote_ids
        ? JSON.parse(this.quoteDetails.children_quote_ids)
        : [];

      this.quoteDetails.parent_quote_details = this.quoteDetails?.parent_quote_id
        ? JSON.parse(this.quoteDetails.parent_quote_id)
        : null;

      this.isDetailsLoaded = true;
    });
  }

  /// Quote Assets
  async getQuoteAsset() {
    await this.quoteService.getQuoteAsset(this.quoteId).then((res: any) => {
      if (res && res.length > 0) {
        this.quoteAssets = res;
      }
    });
  }

  /// Quote Assets Line Items
  async getQuoteLineItem() {
    await this.quoteService.getQuoteLineItem(this.quoteId).then((res: any) => {
      if (res && res.length > 0) {
        this.quoteAssetsLineItems = res;
      }
    });
  }

  /// Quote Assets Details
  organizeAssetDetails() {
    this.combinedAssetsDetails = this.quoteAssets.map(asset => {
      const correctivePricing = this.quoteAssetsLineItems.filter(item => item.asset_id === asset.asset_id);

      return {
        ...asset,
        correctivePricing
      };
    });
  }

  /// Quote Custom Fields (Pick Tick. No)
  async getQuoteCustomFields() {
    this.quoteService.getQuoteCustomFields(this.quoteId).then((res: any) => {
      if (res && res.length > 0) {
        this.quoteCustomFields = res;
      }
    })
  }

  /// Quote History Details
  async getQuoteHistories() {
    this.loadingHistory = true;
    await this.quoteService.getQuoteHistory(this.quoteId).then((response: any) => {
      this.loadingHistory = false;
      if (!Array.isArray(response)) {
        this.toastService.showToast('Failed to load quote history', 'error');
        return;
      }
      this.historyTableData = {
        data: response.map((item: any) => ({
          history_id: item.quote_history_id,
          action_description: item.action_description,
          user_name: item.created_first_name + ' ' + item.created_last_name,
          email: item.user_email,
          role_name: item.role_name,
          date: DateUtils.formatDateTime(item.modified_dt)
        }))
      };
    });
  }

  async getQuoteAttachments() {
    await this.quoteService.getQuoteAttachments(this.quoteId, 5, 0).then((data: any) => {
      if (!data || !Array.isArray(data.Data)) return;
      this.quoteAttachments = data.Data;
    });
  }

  changeTab(tab: 'assets' | 'attachments' | 'comments' | 'history') {
    this.activeTab = tab;
  }

  get plainDescription(): string {
    const html = this.quoteDetails?.quote_description || '';
    const text = html
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (!text || text === '<p><br></p>') return '--';
    return text;
  }

  constructQuoteDetailsFields(): { key: string; value: KeyValuePairTableValueType }[] {
    const data: { key: string; value: KeyValuePairTableValueType }[] = [
      {
        key: 'Status',
        value: new TemplateAndValue(this.tagField, {
          name: this.quoteDetails?.status,
          color: this.quoteDetails?.status_color
        })
      },
      {key: 'Description', value: new TemplateAndValue(this.descriptionField, this.plainDescription)},
      {
        key: 'Location',
        value: new TemplateAndValue(this.locationField, {
          name: this.quoteDetails?.location_name,
          address: this.quoteDetails?.customer_shipping_address
        })
      },
      {key: 'Job Type', value: new TemplateAndValue(this.boldField, this.quoteDetails?.job_type || '--')},
      {
        key: 'Job Number',
        value: this.quoteDetails?.job_number
          ? new PageRedirect('/job', this.quoteDetails?.job_number, {job_id: this.quoteDetails?.job_id})
          : ''
      },
      {
        key: 'Expiration Date',
        value: new TemplateAndValue(this.boldField, DateUtils.formatDate(this.quoteDetails?.expiration_date))
      },
      {key: 'Order No.', value: this.quoteDetails?.order_number || ''},
      {key: 'Renew Pick Ticket No.', value: this.quoteDetails?.renew_pick_ticket_number || ''}
    ];

    for (const field of this.quoteCustomFields) {
      data.push({
        key: field.display_name,
        value: field?.value || ''
      });
    }

    return data;
  }

  /// Calculation (only for selected items)
  calculateAssetSubtotal(pricingItems: any[]): number {
    const selectedItems = this.getSelectedItems(pricingItems);
    return selectedItems.reduce((acc, item) => acc + (item.price || 0), 0);
  }

  calculateTotalTax(pricingItems: any[]): number {
    const selectedItems = this.getSelectedItems(pricingItems);
    return selectedItems.reduce((acc, item) => acc + (item.tax || 0), 0);
  }

  calculateTotalWithTax(pricingItems: any[]): number {
    const subtotal = this.calculateAssetSubtotal(pricingItems);
    const totalTax = this.calculateTotalTax(pricingItems);
    return subtotal + totalTax;
  }

  calculateTotalSubtotalForAllAssets(): number {
    return this.combinedAssetsDetails.reduce((acc, asset) => {
      const assetSubtotal = this.calculateAssetSubtotal(asset.correctivePricing);
      return acc + assetSubtotal;
    }, 0);
  }

  calculateTotalTaxForAllAssets(): number {
    return this.combinedAssetsDetails.reduce((acc, asset) => {
      const assetTax = this.calculateTotalTax(asset.correctivePricing);
      return acc + assetTax;
    }, 0);
  }

  calculateTotalWithTaxForAllAssets(): number {
    const totalSubtotal = this.calculateTotalSubtotalForAllAssets();
    const totalTax = this.calculateTotalTaxForAllAssets();
    return (totalSubtotal + totalTax);
  }

  formatCurrency(value: number): string {
    return `$${value.toFixed(2)}`;
  }

  getTotalNumber(value: any) {
    return value.length > 0 ? value[0].total_count : 0;
  }

  /// Redirect Methods
  goToQuote(quoteId: string) {
    this.router.navigate(['/quote'], {queryParams: {quote_id: quoteId}}).then(() => {
      if (this.router.url === `/quote?quote_id=${quoteId}`) {
        this.loadQuoteDetails(quoteId); //reload all data on this page
      }
    });
  }

  goToJob(jobId: string) {
    this.router.navigate(['/job'], {queryParams: {job_id: jobId}});
  }

  returnToQuotes() {
    this.router.navigate(['/quotes']);
  }

  /// For UI logic
  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  @HostListener('document:click', ['$event'])
  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  showJobNumber(quoteId: string) {
    this.hoveredQuoteId = quoteId;
  }

  hideJobNumber() {
    this.hoveredQuoteId = null;
  }

  createComment(commentContent: string) {
    this.quoteService.createQuoteComment(this.quoteId, commentContent).then(() => {
      this.loadingComments = true;
      this.getQuoteComments();
    });
  }

  editComment(data: { commentId: string; commentContent: string }) {
    this.quoteService.updateQuoteComment(this.quoteId, data.commentId, data.commentContent).then(() => {
      this.loadingComments = true;
      this.getQuoteComments();
    });
  }

  deleteComment(commentId: string) {
    this.quoteService.deleteQuoteComment(this.quoteId, commentId).then(() => {
      this.loadingComments = true;
      this.getQuoteComments();
    })
  }

  /// Selection handling for recommended items
  getSelectedValue(quoteLineItemId: number): boolean {
    return !this.deselectedItems.has(quoteLineItemId);
  }

  onSelectionChange(quoteLineItemId: number, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      this.deselectedItems.delete(quoteLineItemId);
    } else {
      this.deselectedItems.add(quoteLineItemId);
    }
  }

  getSelectedItems(pricingItems: any[]): any[] {
    return pricingItems.filter(item =>
      item.is_mandatory || !this.deselectedItems.has(item.quote_line_item_id)
    );
  }
}
